import { Link } from "react-router-dom";
import {
  Network, ServerCog, Layers, Boxes, Check, ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/lib/site-data";
import SectionHeader from "@/components/SectionHeader";

const ICONS = { Network, ServerCog, Layers, Boxes };

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative bg-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">
              Services
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02]">
              Complete IT engineering — <span className="text-red-600">from rack to cloud.</span>
            </h1>
            <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-3xl">
              We deliver hands-on, certified expertise across the entire enterprise IT stack.
              Whether you need a single field engineer or a full managed-services contract — we
              scale to your operational reality.
            </p>
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">
          {SERVICES.map((s, idx) => {
            const Ic = ICONS[s.icon];
            const flip = idx % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`service-detail-${s.slug}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden grid lg:grid-cols-12 gap-0"
              >
                <div className={`lg:col-span-5 p-10 lg:p-12 ${flip ? "lg:order-2" : ""} bg-slate-950 text-white grid-bg-dark relative overflow-hidden`}>
                  <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-blue-700/30 blur-3xl pointer-events-none" />
                  <div className="relative">
                    <div className="font-mono text-xs uppercase tracking-widest text-blue-300">
                      0{idx + 1} / 04
                    </div>
                    <div className="mt-6 h-14 w-14 rounded-lg bg-red-600/20 text-red-400 flex items-center justify-center">
                      {Ic && <Ic className="h-7 w-7" />}
                    </div>
                    <h2 className="mt-6 font-display text-3xl lg:text-4xl font-black tracking-tighter leading-tight">
                      {s.title}
                    </h2>
                    <p className="mt-4 text-slate-300 leading-relaxed">{s.short}</p>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.slug}`}
                      className="mt-8 inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-blue-50 px-5 py-3 rounded-md font-semibold transition-all"
                    >
                      Get scoped <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="lg:col-span-7 p-10 lg:p-12">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">
                    What we deliver
                  </p>
                  <ul className="space-y-4">
                    {s.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3" data-testid={`service-point-${s.slug}-${pt.slice(0,12).replace(/\s+/g,'-').toLowerCase()}`}>
                        <span className="mt-1 h-5 w-5 rounded-full bg-blue-50 text-blue-900 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                        <span className="text-slate-700 leading-relaxed">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 lg:py-28" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            eyebrow="How we engage"
            title="A simple, transparent delivery model."
            testid="process-header"
          />
          <div className="mt-14 grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Discover", desc: "We map your infrastructure, pain points and SLA needs." },
              { step: "02", title: "Design", desc: "We propose a tailored support model with engineer profiles." },
              { step: "03", title: "Deploy", desc: "Onboarding, runbooks and active monitoring go live." },
              { step: "04", title: "Deliver", desc: "Continuous incident response, reporting & improvements." },
            ].map((p) => (
              <div key={p.step} data-testid={`process-step-${p.step}`} className="border border-slate-200 rounded-xl p-7 hover:border-blue-300 transition-colors">
                <div className="font-mono text-sm text-red-600 font-bold mb-4">{p.step}</div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
